<template>
  <div v-loading="loading">
    <div class="my-4 space-y-2 bg-brand-sand rounded-md shadow-sm px-4 py-4">
      <p class="text-brand-black font-medium">
        Please use
        <a :href="isClassic ? paddleClassicDocs : paddleBillingDocs" target="_blank" class="underline text-brand-blue font-semibold">
          our guide for connecting your Paddle account </a
        >.
      </p>
      <p class="text-brand-brown text-sm font-medium">
        Your account specific webhook URL (for step 3):
        <span v-if="isClassic" class="text-brand-blue font-semibold">
          https://billing.churnkey.co/e/{{ paddleClassicSource }}?appId={{ $store.state.org.appId }}
        </span>
        <span v-else class="text-brand-blue font-semibold">
          https://billing.churnkey.co/e/{{ paddleBillingSource }}?appId={{ $store.state.org.appId }}
        </span>
      </p>
    </div>
    <div class="space-y-6 divide-y-2 divide-brand-sand">
      <div>
        <div class="header flex items-center space-x-2">
          <span> Paddle Version </span>
        </div>
        <PaddleVersion :paddleVersion="version" @save="saveVersion" />
      </div>
      <div class="pt-4">
        <div class="header flex items-center space-x-2">
          <PaddleStatus :badCreds="liveBadCreds" :validCreds="liveValidated" />
          <span> Live Mode </span>
        </div>
        <PaddleCredentials
          mode="live"
          :version="version"
          :credentials="liveCredentials"
          :loading="isLoadingLive"
          @save="saveCredentials"
          @check="checkLiveMode"
        />
      </div>

      <div class="pt-4">
        <div class="header flex items-center space-x-2">
          <PaddleStatus :badCreds="testBadCreds" :validCreds="testValidated" />
          <span> Sandbox Mode </span>
        </div>
        <PaddleCredentials
          mode="test"
          :version="version"
          :credentials="testCredentials"
          :loading="isLoadingTest"
          @save="saveCredentials"
          @check="checkTestMode"
        />
      </div>
    </div>
  </div>
</template>

<script>
import OrgService from '@/api/org.service';
import { SYNC_ORG } from '@/store/action-types';
import PaddleCredentials from '@/components/Settings/Paddle/PaddleCredentials.vue';
import PaddleStatus from '@/components/Settings/Paddle/PaddleStatus.vue';
import PaddleVersion from '@/components/Settings/Paddle/PaddleVersion.vue';
import { PADDLE_VERSION } from '@/store/shared';

const paddleClassicSource = 'src_SP8oUSlSZJab';
const paddleBillingSource = 'src_e0rghrqmqzovw2';
const paddleClassicDocs = 'https://docs.churnkey.co/paddle';
const paddleBillingDocs = 'https://docs.churnkey.co/paddle-billing';

export default {
  name: 'PaddleConnect',
  components: {
    PaddleCredentials,
    PaddleStatus,
    PaddleVersion,
  },
  data() {
    return {
      loading: true,
      isLoadingLive: false,
      isLoadingTest: false,
      // Connection Flags
      testValidated: false,
      liveValidated: false,
      liveBadCreds: false,
      testBadCreds: false,
      paddleClassicSource,
      paddleBillingSource,
      paddleClassicDocs,
      paddleBillingDocs,
    };
  },
  async created() {
    try {
      await OrgService.testPaddleConnect(this.user.org, 'test');
      this.testValidated = true;
    } catch {
      this.testValidated = false;
      if (this.hasAllTestCredentials) {
        this.testBadCreds = true;
      }
    }
    try {
      await OrgService.testPaddleConnect(this.user.org, 'live');
      this.liveValidated = true;
    } catch {
      this.liveValidated = false;
      if (this.hasAllLiveCredentials) {
        this.liveBadCreds = true;
      }
    }
    this.loading = false;
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    org() {
      return this.$store.state.org;
    },
    version() {
      return this.$store.state.org.paddleVersion;
    },
    isClassic() {
      return this.version === PADDLE_VERSION.CLASSIC;
    },
    testCredentials() {
      if (this.org?.secrets?.test) {
        return {
          paddleVendorId: this.org.secrets.test.paddleVendorId,
          paddleAuthCode: this.org.secrets.test.paddleAuthCode,
          paddlePublicKey: this.org.secrets.test.paddlePublicKey,
          paddleSecret: this.org.secrets.test.paddleSecret,
          paddleWebhookSecret: this.org.secrets.test.paddleWebhookSecret,
        };
      }
      return {};
    },
    liveCredentials() {
      if (this.org?.secrets?.prod) {
        return {
          paddleVendorId: this.org.secrets.prod.paddleVendorId,
          paddleAuthCode: this.org.secrets.prod.paddleAuthCode,
          paddlePublicKey: this.org.secrets.prod.paddlePublicKey,
          paddleSecret: this.org.secrets.prod.paddleSecret,
          paddleWebhookSecret: this.org.secrets.prod.paddleWebhookSecret,
        };
      }
      return {};
    },
    hasAllLiveCredentials() {
      const { paddleVendorId, paddleAuthCode, paddlePublicKey, paddleSecret, paddleWebhookSecret } = this.org.secrets.prod;
      if (this.isClassic) {
        return !!(paddleVendorId && paddleAuthCode && paddlePublicKey);
      }
      return !!(paddleSecret && paddleWebhookSecret);
    },
    hasAllTestCredentials() {
      const { paddleVendorId, paddleAuthCode, paddlePublicKey, paddleSecret, paddleWebhookSecret } = this.org.secrets.test;
      if (this.isClassic) {
        return !!(paddleVendorId && paddleAuthCode && paddlePublicKey);
      }
      return !!(paddleSecret && paddleWebhookSecret);
    },
  },
  methods: {
    deactivate() {
      this.$notify({ title: 'Deactivation Queued', type: 'warning' });
    },
    async saveVersion(value) {
      try {
        this.loading = true;
        await OrgService.savePaddleVersion(this.user.org, value);
        this.$notify({ title: 'Paddle Version Saved', type: 'success' });
        await this.$store.dispatch(SYNC_ORG);
      } catch (error) {
        console.log('Error', error);
        this.$notify({ title: 'Paddle Version Update Failed', type: 'error' });
      } finally {
        // reset check status
        this.testValidated = false;
        this.liveValidated = false;
        this.liveBadCreds = false;
        this.testBadCreds = false;
        if (this.hasAllTestCredentials) await this.checkTestMode();
        if (this.hasAllLiveCredentials) await this.checkLiveMode();
        this.loading = false;
      }
    },
    async saveCredentials({ mode, credentials }) {
      try {
        const secrets = {};
        this.loading = true;
        if (mode === 'live') {
          secrets.prod = credentials;
        } else if (mode === 'test') {
          secrets.test = credentials;
        }
        const result = await OrgService.savePaddleSecrets(this.user.org, secrets);
        this.$notify({ title: 'Paddle Credentials Saved', type: 'success' });
        await this.$store.dispatch(SYNC_ORG);
      } catch (error) {
        console.log(error);
        this.$notify({ title: 'Paddle Credentials Update Failed', type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    async checkTestMode() {
      this.isLoadingTest = true;
      try {
        await OrgService.testPaddleConnect(this.user.org, 'test');
        this.$notify({ title: 'Test Mode Connected', type: 'success' });
        this.testValidated = true;
        this.testBadCreds = false;
      } catch (error) {
        this.$notify({ title: 'Error', message: 'Paddle Test Connection Failed', type: 'error' });
        this.testValidated = false;
        this.testBadCreds = true;
        console.log('Error', error);
      }
      this.isLoadingTest = false;
    },
    async checkLiveMode() {
      this.isLoadingLive = true;
      try {
        await OrgService.testPaddleConnect(this.user.org, 'live');
        this.liveValidated = true;
        this.liveBadCreds = false;
        this.$notify({ title: 'Live Mode Connected', type: 'success' });
      } catch (error) {
        this.$notify({ title: 'Error', message: 'Paddle Live Connection Failed', type: 'error' });
        console.log('Error', error);
        this.liveValidated = false;
        this.liveBadCreds = true;
      }
      this.isLoadingLive = false;
    },
  },
};
</script>
